<template>
  <div class="grouped__product">
    <AppLink :to="productInfo?.url">
      <div class="grouped__product-visual">
        <img
          :src="image"
          :alt="item?.product.name"
        >
      </div>
    </AppLink>
    <div class="grouped__product-info">
      <ProductGroupedInfo
        :product-info="productInfo"
        :product-prices="productPrices"
      />

      <div class="grouped__product-options">
        <ProductConfigurable
          v-if="item.product?.__typename === 'ConfigurableProduct'"
          :key="item.product?.sku"
          :product="item.product"
          parent-type="GroupedProduct"
          @selected="collect"
        />
        <ProductSimple
          v-else
          :key="item.product?.sku"
          :product="item.product"
          :optional="false"
          @selected="collect"
        />

        <button
          type="button"
          :disabled="pending || !Object.values(selectedItems).some(Boolean)"
          class="add-to-cart__cta add-to-cart__grouped button button--shop-flow button--icon-only"
          data-testid="add-to-cart"
          @click.prevent="add"
        >
          <IconShoppingcart
            class="add-to-cart__icon icon icon--shoppingcart"
          />
          <span>{{ $t('Add to cart') }}</span>
        </button>
      </div>

      <div
        v-if="hasError"
        ref="error"
        class="message message--error required"
      >
        <IconInfo class="message__icon icon icon--info" />
        <span>{{ $t('Please choose a size') }}</span>
      </div>
    </div>
    <slot />
  </div>
</template>

<script lang="ts" setup>
import type { MagentoCartItem, MagentoCartItemInput } from '~/app/types/magento'

export interface Props {
  item: MagentoCartItem
}

const { public: config } = useRuntimeConfig()
const { addToCart, pending } = useCart()
const hasError = ref(false)
const error = ref(null)
const props = defineProps<Props>()

onClickOutside(error, () => {
  hasError.value = false
})

const emit = defineEmits<{
  selected: Record<string, MagentoCartItemInput>
}>()

const selectedItems = ref<Record<string, MagentoCartItemInput>>({})

watch(selectedItems, () => {
  emit('selected', selectedItems.value)
})

const collect = (item: Record<string, MagentoCartItemInput>) => {
  selectedItems.value = {
    ...selectedItems.value,
    ...item,
  }
}

const productInfo = computed<{ name: string, descriptive: string, sku: string, url: string }>(() => {
  return {
    name: props.item?.product?.product?.name || '',
    brand: props.item?.product?.product?.brand || '',
    sku: props.item?.product?.product?.sku || '',
    url: props.item?.product?.product?.url || '',
  }
})

const productPrices = computed<{ regularPrice: number, finalPrice: number }>(() => {
  return {
    regularPrice: props.item?.product?.price_range?.minimum_price?.regular_price?.value,
    finalPrice: props.item?.product?.price_range?.minimum_price?.final_price?.value,
  }
})

const variant = computed(() => props.item.product?.variants?.[0]?.product)

const image = computed<string>(() => {
  if (variant.value?.image?.url && !variant.value?.image?.url.includes('placeholder')) {
    return variant.value?.image?.url.replace('FD/-/', 'OQ75_RM170,1066,255,255,255/-/')
  }

  return `${config.xcdnUrl}OQ75_RM830,1066,255,255,255/-/catalog/product/placeholder/default/Product.FallBack.jpg`
})

const add = () => {
  const item = Object.values(selectedItems.value).filter(item => item)
  if (!item.length) {
    hasError.value = true
  }
  else {
    addToCart(item, false)
    hasError.value = false
  }
}
</script>
